import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { fetchBrands } from "../store/slices/brandsSlice";
import { Section, Container, SectionEyebrow } from "../components/ui/Section";
import {
  StaggerReveal,
  MotionItem,
  fadeUp,
  fadeUpSmall,
  scaleIn,
} from "../components/ui/Motion";
import { BrandCardSkeleton } from "../components/ui/Skeleton";
import {
  PageHeader,
  Title,
  HeaderSub,
  Grid,
  BrandCard,
  LogoArea,
  LogoText,
  CardBody,
  BrandName,
  BrandOrigin,
  BrandDesc,
  EmptyState,
  ViewLabel,
} from "./styles/BrandsPage.styles";
import { ReactComponent as SuitcaseIcon } from "../assets/icons/suitcase.svg";
import { ReactComponent as PrincessLogo } from "../assets/icons/princess.svg";
import { ReactComponent as NexLogo } from "../assets/icons/nex.svg";
import { ReactComponent as RibcoLogo } from "../assets/icons/ribco.svg";
import { ReactComponent as AzimutLogo } from "../assets/icons/azimut.svg";
import { ReactComponent as FerrettiLogo } from "../assets/icons/ferretti.svg";
import { ReactComponent as MaioraLogo } from "../assets/icons/maiora.svg";
import { ReactComponent as BenettiLogo } from "../assets/icons/benetti.svg";
import { ReactComponent as SunseekerLogo } from "../assets/icons/sunseeker.svg";
import { mediaUrl } from "../utils/media";

const BRAND_LOGO_SVGS = {
  princess: PrincessLogo,
  nex: NexLogo,
  ribco: RibcoLogo,
  azimut: AzimutLogo,
  ferretti: FerrettiLogo,
  maiora: MaioraLogo,
  benetti: BenettiLogo,
  sunseeker: SunseekerLogo,
};

function BrandsPage() {
  const dispatch = useDispatch();
  const { items: brands, loading } = useSelector((state) => state.brands);

  useEffect(() => {
    if (brands.length === 0) {
      dispatch(fetchBrands());
    }
  }, [dispatch, brands.length]);

  const renderBrandCard = (brand) => {
    const LogoSvg = BRAND_LOGO_SVGS[brand.slug];
    return (
      <MotionItem
        key={brand.id}
        $variants={scaleIn}
        style={{ height: "100%", display: "flex", flexDirection: "column" }}
      >
        <Link
          to={`/brands/${brand.slug || brand.id}`}
          style={{ display: "flex", flex: 1, textDecoration: "none" }}
        >
          <BrandCard>
            <LogoArea>
              {LogoSvg ? (
                <LogoSvg aria-label={brand.name} />
              ) : brand.logo ? (
                <img src={mediaUrl(brand.logo)} alt={brand.name} />
              ) : (
                <LogoText>{brand.name}</LogoText>
              )}
            </LogoArea>
            <CardBody>
              <BrandName>{brand.name}</BrandName>
              {brand.origin && <BrandOrigin>{brand.origin}</BrandOrigin>}
              <BrandDesc>{brand.description || ""}</BrandDesc>
              <ViewLabel>Explore {brand.name}</ViewLabel>
            </CardBody>
          </BrandCard>
        </Link>
      </MotionItem>
    );
  };

  return (
    <>
      <PageHeader>
        <Container>
          <StaggerReveal>
            <MotionItem $variants={fadeUpSmall}>
              <SectionEyebrow>Our Partners</SectionEyebrow>
            </MotionItem>
            <MotionItem $variants={fadeUp}>
              <Title>World-Class Brands</Title>
            </MotionItem>
            <MotionItem $variants={fadeUpSmall}>
              <HeaderSub>
                We partner with the most prestigious shipyards to bring you an
                exceptional selection.
              </HeaderSub>
            </MotionItem>
          </StaggerReveal>
        </Container>
      </PageHeader>

      <Section $bg="offWhite">
        <Container>
          {loading ? (
            <Grid as="div">
              {Array.from({ length: 6 }).map((_, i) => (
                <BrandCardSkeleton key={i} />
              ))}
            </Grid>
          ) : brands.length === 0 ? (
            <EmptyState>
              <SuitcaseIcon />
              <p>No brands available</p>
              <span>
                Brand partnerships are being established. Check back soon.
              </span>
            </EmptyState>
          ) : (
            <Grid>{brands.map(renderBrandCard)}</Grid>
          )}
        </Container>
      </Section>
    </>
  );
}

export default BrandsPage;
